import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, Phone, Mail } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-gray-900 to-black py-12 sm:py-16">
          <div className="container mx-auto px-4 text-center">
            <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Terms & <span className="text-yellow-500">Conditions</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
              Please read these terms carefully before using our services
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-8 sm:py-12">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10">
            
            {/* Last Updated */}
            <p className="text-gray-500 text-sm mb-8">Last Updated: January 2025</p>

            {/* Introduction */}
            <section className="mb-8">
              <p className="text-gray-700 leading-relaxed">
                Welcome to <strong>Jain Watch Company</strong>. These Terms and Conditions govern your use of our website and the purchase of products from our online store. By accessing our website or placing an order, you agree to be bound by these terms.
              </p>
            </section>

            {/* General Terms */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">1. General Terms</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  By using this website, you confirm that you are at least 18 years of age or are accessing the site under the supervision of a parent or guardian.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Jain Watch Company reserves the right to modify these terms at any time without prior notice. Continued use of the website after changes constitutes acceptance of the new terms.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  All content on this website, including images, text, logos, and graphics, is the property of Jain Watch Company and is protected by Indian copyright laws.
                </li>
              </ul>
            </section>

            {/* Products & Pricing */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">2. Products & Pricing</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  All products displayed on our website are subject to availability. We reserve the right to discontinue any product at any time.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Prices are listed in Indian Rupees (INR) and include applicable GST unless otherwise stated.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  We strive to provide accurate product descriptions and images. However, slight variations in color may occur due to screen settings.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  We reserve the right to correct pricing errors. If a product is listed at an incorrect price, we will contact you before processing the order.
                </li>
              </ul>
            </section>

            {/* Orders & Payment */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">3. Orders & Payment</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Placing an order on our website constitutes an offer to purchase. Order confirmation is subject to our acceptance.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  We accept payments through Razorpay, which includes UPI, credit cards, debit cards, net banking, and wallets.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  All online transactions are processed through secure, encrypted payment gateways. Jain Watch Company does not store your card details.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  We reserve the right to cancel or refuse any order if we suspect fraud, unauthorized transactions, or any violation of these terms.
                </li>
              </ul>
            </section>

            {/* User Account */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">4. User Responsibilities</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  You are responsible for providing accurate and complete information during the checkout process.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  You agree not to use our website for any unlawful purpose or in a way that could damage, disable, or impair the website.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  You must not attempt to gain unauthorized access to any part of the website, other accounts, or systems.
                </li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  All trademarks, logos, brand names, and product images displayed on this website are the property of their respective owners.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  You may not reproduce, distribute, or use any content from this website without prior written permission from Jain Watch Company.
                </li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">6. Limitation of Liability</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 sm:p-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Jain Watch Company shall not be liable for any indirect, incidental, or consequential damages arising from the use of our website or products.
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    We are not responsible for delays or failures in performance resulting from circumstances beyond our reasonable control, including natural disasters, strikes, or government actions.
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Our maximum liability for any claim shall not exceed the amount paid by you for the specific product giving rise to such claim.
                  </li>
                </ul>
              </div>
            </section>

            {/* Third-Party Links */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">7. Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites for your convenience. These links are provided for reference only. Jain Watch Company is not responsible for the content, privacy policies, or practices of any third-party websites.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">8. Governing Law & Jurisdiction</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  These terms shall be governed by and construed in accordance with the laws of India.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Any disputes arising from these terms or your use of the website shall be subject to the exclusive jurisdiction of the courts in Muzaffarnagar, Uttar Pradesh, India.
                </li>
              </ul>
            </section>

            {/* Severability */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">9. Severability</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms and Conditions is found to be invalid or unenforceable, the remaining provisions shall continue to be valid and enforceable to the fullest extent permitted by law.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-gray-900 text-white rounded-lg p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Contact Us</h2>
              <p className="text-gray-300 mb-4">For questions about these Terms and Conditions, please contact:</p>
              <div className="space-y-3">
                <p className="text-white font-semibold">Jain Watch Company</p>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-yellow-500 mr-3" />
                  <a href="mailto:jainwatchcompany1975@gmail.com" className="text-yellow-500 hover:underline">
                    jainwatchcompany1975@gmail.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-yellow-500 mr-3" />
                  <a href="tel:9927058909" className="text-yellow-500 hover:underline">
                    +91 9927058909
                  </a>
                </div>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsConditions;
